import type { DataAttributes } from 'shared/lib/data-auto-test';
import { dateToString, endOfDay, getDefaultEndDate, getDefaultStartDate, stringToDate } from 'shared/lib/format';
import { isNull } from 'shared/lib/type-guards';
import { useUrlResolver } from 'shared/lib/url';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useUrlParamCommands } from 'shared/ui/MultiSelect/Multiselect/utils';

import type { PickerValue } from './date-ranges';
import type { DatePickerProps } from './DatePickerRange';
import { DatePickerRange } from './DatePickerRange';

type FilterKeys = `filters[${string}]`;

type DatePickerRangeWithQueriesProps = Omit<DatePickerProps, 'onChange' | 'value'> & {
  fromKey: FilterKeys;
  toKey: FilterKeys;
  preventDefaultDateSet?: boolean;
  wrapperClass?: string;
  attributes?: DataAttributes['attributes'];
  isWhite?: boolean;
  initialFrom?: Date;
  initialTo?: Date;
};

export const REMOVE_DEFAULT_DATE = 'default-date';

export function DatePickerWithQueriesUrl({
  fromKey,
  toKey,
  wrapperClass,
  attributes,
  preventDefaultDateSet,
  initialFrom,
  initialTo,
  ...rest
}: DatePickerRangeWithQueriesProps): JSX.Element {
  const { removeParamFromUrl, getParam, getUrl, addParamToUrl, updateHistory } = useUrlResolver();
  const [value, setDateRange] = useState<PickerValue[]>([null, null]);
  const { paginationReset } = useUrlParamCommands();

  useLayoutEffect(() => {
    let url = getUrl();
    const hasParams = getParam(REMOVE_DEFAULT_DATE);

    if (!preventDefaultDateSet && !hasParams) {
      const hasFrom = getParam(fromKey);
      const hasTo = getParam(toKey);

      if (!hasFrom) {
        addParamToUrl({ name: fromKey, value: `${dateToString(initialFrom ?? getDefaultStartDate())}`, url });
      }
      if (!hasTo) {
        addParamToUrl({ name: toKey, value: `${dateToString(initialTo ?? getDefaultEndDate())}`, url });
      }
      if (!hasFrom || !hasTo) {
        paginationReset(url);
      }

      updateHistory({ url });
    }

    const hasUpdatedFrom = getParam(fromKey);
    const hasUpdatedTo = getParam(toKey);

    if (hasUpdatedFrom && hasUpdatedTo) {
      setDateRange([stringToDate(hasUpdatedFrom), stringToDate(hasUpdatedTo)]);
    }
  }, [preventDefaultDateSet]);

  useEffect(() => {
    window.addEventListener('popstate', handleBack);

    return () => {
      window.removeEventListener('popstate', handleBack);
    };
  }, []);

  const getFilterValues = (): PickerValue[] => {
    const fromDateString = getParam(fromKey);
    const toDateString = getParam(toKey);
    const fromDateParamsFilter = fromDateString ? stringToDate(fromDateString) : null;
    const toDateParamsFilter = toDateString ? stringToDate(toDateString) : null;

    if (fromDateParamsFilter && toDateParamsFilter) {
      return [fromDateParamsFilter, toDateParamsFilter];
    }

    return [null, null];
  };

  const handleBack = () => {
    const value = getFilterValues();

    setDateRange(value);
  };

  const onChangeHandler = (value: PickerValue[]) => {
    const [from, to] = value;
    const url = getUrl();

    paginationReset(url);
    if (!isNull(from) && !isNull(to)) {
      addParamToUrl({ name: fromKey, value: dateToString(from), url });
      addParamToUrl({ name: toKey, value: dateToString(endOfDay(to)), url });
      removeParamFromUrl({ name: REMOVE_DEFAULT_DATE, url });
    }

    if (isNull(from) && isNull(to)) {
      removeParamFromUrl({ name: fromKey, url });
      removeParamFromUrl({ name: toKey, url });
      addParamToUrl({ name: REMOVE_DEFAULT_DATE, value: 'false', url });
    }

    updateHistory({ url });
    setDateRange(value);
  };

  return (
    <div
      className={wrapperClass}
      {...attributes}
    >
      <DatePickerRange
        {...rest}
        placeholder="Date range"
        onChange={onChangeHandler}
        value={value}
      />
    </div>
  );
}
